import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { LogIn, Home, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeProvider } from '@/contexts/ThemeContext';
import { signOut } from '@/services/logoutService';
import { auth } from '@/lib/firebase';

const DUCK_FACTS = [
  "Ducks sometimes need a break too! Your session has ended.",
  "Even the best ducks eventually have to log out for a swim.",
  "Quack! You've been logged out. Time to paddle back in!",
  "Duck fact: Logging out helps keep your feathers safe!"
];

/**
 * Clears localStorage, sessionStorage, and all IndexedDB databases for a thorough logout.
 * Handles browsers with/without indexedDB.databases() support.
 */
async function clearAppStorage() {
  // Clear localStorage
  try {
    localStorage.clear();
  } catch (e) {
    console.warn('localStorage.clear() failed:', e);
  }

  // Clear sessionStorage
  try {
    sessionStorage.clear();
  } catch (error) {
    console.error('Failed to clear app storage:', error);
  }
}

export default function Logout() {
  // Check for forced logout
  const [searchParams] = useSearchParams();
  const isForced = searchParams.get('forced') === '1';
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Initialize with a random duck fact immediately to prevent changes
  const [duckFact, setDuckFact] = useState(
    () => DUCK_FACTS[Math.floor(Math.random() * DUCK_FACTS.length)]
  );
  const [redirectUrl, setRedirectUrl] = useState<string>('/login');
  const hasStartedLogout = useRef(false);
  const minLoadingTime = useRef(2000); // Minimum 2 seconds to show the loading screen
  const loadingStartTime = useRef(Date.now());

  const performLogout = useCallback(async () => {
    // Prevent multiple sign-out attempts
    if (hasStartedLogout.current) return;
    hasStartedLogout.current = true;

    let isMounted = true;
    let unsubscribe: (() => void) | null = null;

    try {
      // Wait for auth state to be ready with a timeout
      await new Promise<void>((resolve) => {
        const timer = setTimeout(() => {
          if (isMounted) {
            console.log('Auth state check timeout');
            if (unsubscribe) unsubscribe();
            resolve();
          }
        }, 3000);

        unsubscribe = auth.onAuthStateChanged((user) => {
          clearTimeout(timer);
          if (isMounted) {
            if (unsubscribe) unsubscribe();
            resolve();
          }
        });
      });

      // Calculate remaining time to ensure minimum loading duration
      const elapsed = Date.now() - loadingStartTime.current;
      const remainingTime = Math.max(0, minLoadingTime.current - elapsed);

      // Perform sign out
      try {
        console.log('Attempting to sign out...');
        // --- SESSION-AWARE LOGOUT ---
        await signOut(); // This now deletes Firestore session, clears sessionId/token, etc.
        // Wait for the remaining time or sign out, whichever takes longer
        await new Promise(resolve => setTimeout(resolve, remainingTime));
        console.log('Sign out successful');
      } catch (signOutError) {
        console.error('Error during sign out:', signOutError);
        // Continue even if sign out fails
      }

      // Clear all browser storage (localStorage, sessionStorage, IndexedDB)
      try {
        await clearAppStorage();
        console.log('Cleared localStorage, sessionStorage, and IndexedDB');
      } catch (storageError) {
        console.error('Error clearing browser storage:', storageError);
        // Continue even if storage clearing fails
      }

      // Update UI state
      if (isMounted) {
        const returnUrl = searchParams.get('returnUrl') || '/login';
        setRedirectUrl(returnUrl);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error during logout:', error);
      if (isMounted) {
        setError('Failed to sign out. Please try again.');
        setIsLoading(false);
      }
    }

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, [searchParams]);

  useEffect(() => {
    // Record when we start the logout process
    loadingStartTime.current = Date.now();

    // Only start the logout process if we haven't already
    if (!hasStartedLogout.current) {
      const executeLogout = async () => {
        try {
          await performLogout();
        } catch (error) {
          console.error('Error in logout effect:', error);
          setIsLoading(false);
        }
      };

      executeLogout();
    }

    // Set a timeout to ensure we don't get stuck in loading state
    const timeout = setTimeout(() => {
      if (isLoading) {
        console.warn('Logout timed out');
        setIsLoading(false);
      }
    }, 8000); // 8 second timeout (increased from 5s)

    return () => {
      clearTimeout(timeout);
    };
  }, [performLogout, isLoading]);

  if (isLoading) {
    // Animation for the duck image
    const duckAnimation = {
      animation: 'bounce 1.5s ease-in-out infinite',
      transformOrigin: 'center bottom'
    };

    return (
      <ThemeProvider>
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-accent/5 to-accent/10 dark:from-accent/5 dark:to-accent/10" style={{ margin: 'calc(0px - (var(--spacing) * 4))' }}>
          <div className="relative w-64 h-64 mb-8">
            {/* Duck image */}
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src="/assets/img/404.png"
                alt="Duck"
                className="w-48 h-48 object-contain"
                style={duckAnimation}
              />
            </div>

            {/* Water ripples */}
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="absolute w-10 h-2 bg-accent/50 dark:bg-accent/50 rounded-full"
                  style={{
                    bottom: `${i * 4}px`,
                    left: '50%',
                    transform: `translateX(-50%) scale(${1 - (i * 0.1)})`,
                    opacity: 1 - (i * 0.3),
                    animation: `ripple 1.5s ease-in-out ${i * 0.1}s infinite`
                  }}
                />
              ))}
            </div>

            {/* Animation styles */}
            <style dangerouslySetInnerHTML={{
              __html: `
              @keyframes bounce {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-10px); }
              }
              @keyframes ripple {
                0% { transform: translateX(-50%) scale(1); opacity: 1; }
                100% { transform: translateX(-50%) scale(1.5); opacity: 0; }
              }
            `
            }} />
          </div>

          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">Quacking you out...</h2>
            <p className="text-gray-600 dark:text-gray-300">Just a moment while we waddle you out safely</p>
            <div className="mt-4 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-accent" />
            </div>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              {DUCK_FACTS[Math.floor(Math.random() * DUCK_FACTS.length)]}
            </p>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-accent/5 to-accent/10 dark:from-accent/5 dark:to-accent/10" style={{ margin: 'calc(0px - (var(--spacing) * 4))' }}>
        <div className="text-center max-w-2xl mx-auto">
          <div className="w-64 h-48 mx-auto mb-8 flex items-center justify-center">
            <img
              src="/assets/img/404.png"
              alt="Logout Duck"
              className="h-full w-auto object-contain"
            />
          </div>
          <h1 className="text-5xl font-bold text-accent mb-4">Logged Out</h1>
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-6">
            Quack! Your session has expired or you’ve been logged out.
          </h2>
          {error ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-8">
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => window.location.href = redirectUrl}
                className="gap-2 bg-accent"
                disabled={isLoading}
              >
                <LogIn className="w-4 h-4" />
                Log In Again
              </Button>
            </div>
          )}
          <style dangerouslySetInnerHTML={{
            __html: `
            @keyframes bounce {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-10px); }
            }
            @keyframes ripple {
              0% { transform: translateX(-50%) scale(1); opacity: 1; }
              100% { transform: translateX(-50%) scale(1.5); opacity: 0; }
            }
          `
          }} />
        </div>
      </div>
    </ThemeProvider>
  );
}